import React from 'react'

import {
  Dropdown,
  useResponsiveDropdown,
  MenuIconButton,
  useForDesktop,
  Flex,
  useModalContext,
  downloadFile,
} from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'

import { useIsAnnotationContextAvailable } from '../../../../annotations/hooks'

import { ReactComponent as DotsIcon } from '@sketch/icons/dots-3-horizontal-24'
import { AnnotationDropdownActions } from '../../../../annotations/components'
import { ShareWithoutVersion } from '../../../../versioning'
import { useHeaderActionDeriver } from '../../../hooks/useHeaderActionDeriver'
import CopyLink from '../../../components/ShareActionsDropdown/CopyLink'
import { AppIcon } from '../../../components/EditInSketchButton/EditInSketchButton.styles'

import { LaunchSketchAlert, useLicenseAgreement } from '@sketch/modules-common'
import {
  ArtboardDetailInfoFragment,
  FrameDetailInfoFragment,
  VersionFragment,
} from '@sketch/gql-types'

interface DetailDropdownProps {
  share: ShareWithoutVersion
  currentVersion: VersionFragment | null
  frameGroup: ArtboardDetailInfoFragment | FrameDetailInfoFragment
}

const DetailDropdown = (props: DetailDropdownProps) => {
  const { share, currentVersion } = props
  const isAnnotationsContextAvailable = useIsAnnotationContextAvailable()

  const { hasLicenseAgreement, showLicenseAgreementModal } =
    useLicenseAgreement({
      shareIdentifier: share.identifier,
    })

  const { showDownloadButton, showRemixButton, duplicateShare, download } =
    useHeaderActionDeriver({
      share,
      currentVersion,
    })

  const isDesktop = useForDesktop()
  const { showModal } = useModalContext()
  const userCanOpenInApp = currentVersion?.document?.userCanOpenInApp || false
  const canOpenInApp = userCanOpenInApp && !IS_EMBEDDED

  return (
    <>
      <Dropdown.Header>Frame</Dropdown.Header>

      {!isDesktop && showDownloadButton && hasLicenseAgreement && (
        <Dropdown.Item
          key={1}
          onClick={() =>
            showLicenseAgreementModal({
              agreeAction: () => download.url && downloadFile(download.url),
              helpText:
                'Read and agree to the License Agreement to download this Document.',
            })
          }
        >
          Download
        </Dropdown.Item>
      )}

      {!isDesktop && showDownloadButton && !hasLicenseAgreement && (
        <Dropdown.Download href={download.url}>Download</Dropdown.Download>
      )}

      {!isDesktop &&
        (showRemixButton ? (
          <Dropdown.Item
            onClick={event => {
              event.preventDefault()

              showLicenseAgreementModal({
                agreeAction: duplicateShare,
                helpText:
                  'Read and agree to the License Agreement to remix this Document.',
              })
            }}
          >
            <Flex alignItems="center">
              Remix <AppIcon alt="Sketch" />
            </Flex>
          </Dropdown.Item>
        ) : (
          canOpenInApp && (
            <Dropdown.Item
              onClick={event => {
                event.preventDefault()

                showModal(LaunchSketchAlert, {
                  shareId: share.identifier,
                  action: 'openInSketch',
                })
              }}
            >
              <Flex alignItems="center">
                Edit in <AppIcon alt="Sketch" />
              </Flex>
            </Dropdown.Item>
          )
        ))}

      <CopyLink
        shareIdentifier={share.identifier}
        frameUUID={props.frameGroup.uuid}
      />

      {isAnnotationsContextAvailable && (
        <>
          <Dropdown.Divider />
          <AnnotationDropdownActions
            shareIdentifier={share.identifier}
            subscriptionStatus={share.subscriptionStatus}
            frameGroup={props.frameGroup}
          />
        </>
      )}
    </>
  )
}

export const FrameDetailSettingsDropdown = (props: DetailDropdownProps) => {
  const [dropdown, buttonProps] = useResponsiveDropdown({
    dropdown: DetailDropdown,
    dropdownProps: props,
    placement: 'bottom-end',
  })

  return (
    <>
      <MenuIconButton data-testid="artboard-settings-toggle" {...buttonProps}>
        <DotsIcon role="img" />
        <span className="sr-only">Frame Settings</span>
      </MenuIconButton>
      {dropdown}
    </>
  )
}
